import ListItem from '@tiptap/extension-list-item'

import { ListItemView } from '../node_views/list_item_view'
import { mergeAttributes } from '@tiptap/core'
import { TextSelection } from 'prosemirror-state'

// Max nesting depth for fa-bullet-list (top level + 2 nested = 3 levels).
const MAX_NESTING_LEVELS = 3

// Count bulletList ancestors of the current selection position. A top-level
// list item has 1 (its parent bulletList); each level of nesting adds another.
function bulletListDepth($pos) {
  let count = 0
  for (let d = $pos.depth; d > 0; d--) {
    if ($pos.node(d).type.name === 'bulletList') count++
  }
  return count
}

// Max number of bulletList layers nested inside `node`. Returns 0 for a listItem
// with no nested bulletList; 1 if it contains one bulletList; etc.
function nestedBulletListDepth(node) {
  let max = 0
  node.forEach((child) => {
    const childDepth =
      child.type.name === 'bulletList'
        ? 1 + nestedBulletListDepth(child)
        : nestedBulletListDepth(child)
    if (childDepth > max) max = childDepth
  })
  return max
}

// Insert an empty sibling list item below the current one and move the cursor
// into it. Used as the Enter fallback when splitListItem bails.
function insertEmptySibling(editor) {
  const state = editor.state
  const { $from } = state.selection
  const itemDepth = $from.depth - 1
  const listItem = $from.node(itemDepth)
  if (listItem?.type.name !== 'listItem') return false

  const newItem = listItem.type.createAndFill(listItem.attrs)
  if (!newItem) return false

  const insertPos = $from.after(itemDepth)
  const tr = state.tr.insert(insertPos, newItem)
  // +2 = inside the new listItem's paragraph (skip the listItem and paragraph
  // open tokens) so the cursor lands on a valid text position directly.
  tr.setSelection(TextSelection.near(tr.doc.resolve(insertPos + 2)))
  editor.view.dispatch(tr.scrollIntoView())
  return true
}

export const FaListItem = ListItem.extend({
  content: 'paragraph bulletList*',
  addAttributes() {
    return {
      iconClass: {
        default: 'fa-solid fa-circle',
        parseHTML: (element) => element.getAttribute('data-icon-class'),
        renderHTML: (attributes) => {
          return { 'data-icon-class': attributes.iconClass }
        },
      },
    }
  },
  addCommands() {
    return {
      updateBulletIcon:
        (classes) =>
        ({ tr, _state, dispatch }) => {
          const { doc } = tr

          // Exclude the start and end token
          const nodeSize = doc.nodeSize - 2
          if (doc) {
            doc.nodesBetween(0, nodeSize, (node, pos) => {
              if (
                node.type.name === 'listItem' &&
                node.attrs.iconClass !== classes
              ) {
                tr.setNodeMarkup(pos, null, { iconClass: classes }, node.marks)
              }
            })
          }

          if (tr.docChanged) {
            dispatch?.(tr)
            return true
          }

          return false
        },
    }
  },
  renderHTML({ node, HTMLAttributes, getPos, editor }) {
    return new ListItemView(
      node,
      mergeAttributes(HTMLAttributes, this.options.HTMLAttributes),
      getPos,
      editor
    )
  },
  addNodeView() {
    return ({ node, HTMLAttributes, getPos, editor }) => {
      return new ListItemView(
        node,
        mergeAttributes(HTMLAttributes, this.options.HTMLAttributes),
        getPos,
        editor
      )
    }
  },
  addKeyboardShortcuts() {
    return {
      ...this.parent?.(),

      // Cap nesting at MAX_NESTING_LEVELS. We must check the deepest descendant,
      // not just the cursor's depth — sinking an item that already contains
      // nested lists would push those descendants past the cap. Always consume
      // Tab so it can't bubble out of the editor.
      Tab: () => {
        const { $from } = this.editor.state.selection
        const listItem = $from.node($from.depth - 1)
        if (listItem?.type.name !== 'listItem') return false

        const deepestAfterSink =
          bulletListDepth($from) + 1 + nestedBulletListDepth(listItem)
        if (deepestAfterSink > MAX_NESTING_LEVELS) return true

        this.editor.commands.sinkListItem(this.name)
        return true
      },

      // Default splitListItem bails on empty top-level items (it expects them
      // to be lifted out of the list, but our doc schema is bulletList-only).
      // Fall back to inserting an empty sibling so Enter still works.
      Enter: () => {
        if (this.editor.commands.splitListItem(this.name)) return true
        return insertEmptySibling(this.editor)
      },

      // At the very start of a list item:
      //   nested              → lift one level (un-nest)
      //   top-level + first   → consume (nothing to merge with)
      //   top-level + simple  → explicit merge with previous sibling's paragraph.
      //                         We can't fall through to the default joinItemBackward —
      //                         with our 'paragraph bulletList*' schema, the join
      //                         normalizes by wrapping the current item as a nested
      //                         child of the previous one.
      //   top-level + has nested children → consume (avoid destructive merge)
      Backspace: () => {
        const state = this.editor.state
        const { $from, empty } = state.selection
        const itemDepth = $from.depth - 1
        if (!empty || $from.parentOffset !== 0 || $from.index(itemDepth) !== 0)
          return false

        const listItem = $from.node(itemDepth)
        if (listItem?.type.name !== 'listItem') return false

        if (bulletListDepth($from) >= 2)
          return this.editor.commands.liftListItem(this.name)

        const indexInList = $from.index(itemDepth - 1)
        if (indexInList === 0) return true
        if (listItem.childCount > 1) return true

        const bulletList = $from.node(itemDepth - 1)
        const prevListItem = bulletList.child(indexInList - 1)
        // Bail if either listItem is malformed (e.g. legacy HTML that couldn't
        // be normalized) — without a paragraph as the first child we have no
        // safe place to merge into.
        if (!prevListItem.firstChild || !listItem.firstChild) return true

        const itemStart = $from.before(itemDepth)
        const itemEnd = $from.after(itemDepth)
        const prevItemStart = itemStart - prevListItem.nodeSize
        // Position at the end of the previous paragraph's text content:
        // prevItemStart opens the listItem (+1), the paragraph opens (+1), then
        // the paragraph's text content runs for `content.size` positions.
        const mergePos =
          prevItemStart + 2 + prevListItem.firstChild.content.size
        const inlineContent = listItem.firstChild.content

        const tr = state.tr
          .delete(itemStart, itemEnd)
          .insert(mergePos, inlineContent)
        tr.setSelection(TextSelection.near(tr.doc.resolve(mergePos)))
        this.editor.view.dispatch(tr.scrollIntoView())
        return true
      },
    }
  },
})
